import { useEffect, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getVideoEmbedUrl } from '../lib/videoEmbeds'

type Platform = 'facebook' | 'youtube' | 'spotify' | 'tiktok' | 'instagram'

interface HomeContent {
  heroMediaType: 'image' | 'video'
  heroImageUrl: string
  heroVideoUrl: string
  primaryVideoPlatform: 'facebook' | 'youtube'
  videoOrientation: 'vertical' | 'landscape'
  facebookVideoUrl: string
  youtubeVideoUrl: string
  platformLinks: Record<Platform, string>
}

interface ContentResponse {
  content?: HomeContent
  updatedAt?: string | null
  source?: string
  warning?: string
  error?: string
}

const DEFAULT_CONTENT: HomeContent = {
  heroMediaType: 'image',
  heroImageUrl: '/sunset-1.jpeg',
  heroVideoUrl: '',
  primaryVideoPlatform: 'facebook',
  videoOrientation: 'vertical',
  facebookVideoUrl: 'https://www.facebook.com/share/v/18dTjSbh2F/',
  youtubeVideoUrl: 'https://www.youtube.com/watch?v=mMnQ-ZWF4_I',
  platformLinks: {
    facebook: 'https://www.facebook.com/share/v/18dTjSbh2F/',
    youtube: 'https://www.youtube.com/watch?v=mMnQ-ZWF4_I',
    spotify: '',
    tiktok: '',
    instagram: '',
  },
}

const PLATFORM_LABELS: Array<{ key: Platform; label: string; placeholder: string }> = [
  { key: 'facebook', label: 'Facebook', placeholder: 'https://www.facebook.com/...' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://www.youtube.com/watch?v=...' },
  { key: 'spotify', label: 'Spotify', placeholder: 'https://open.spotify.com/...' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://www.tiktok.com/@...' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://www.instagram.com/...' },
]

const MAX_HERO_VIDEO_BYTES = 60 * 1024 * 1024
const MAX_HERO_IMAGE_BYTES = 12 * 1024 * 1024
const ALLOWED_HERO_VIDEO_TYPES = new Set(['video/mp4', 'video/webm', 'video/quicktime'])
const ALLOWED_HERO_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif'])
const HERO_UPLOAD_HELP_ID = 'hero-upload-help'
const LEGACY_CMS_FROZEN = true

function formatFileSize(bytes: number) {
  return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`
}

function uploadErrorMessage(error: string, maxBytes?: number) {
  if (error === 'unsupported_media_type') return 'Upload a supported hero image or video file.'
  if (error === 'unsupported_image_type') return 'Upload a JPG, PNG, WebP, or AVIF image.'
  if (error === 'unsupported_video_type') return 'Upload an MP4, WebM, or MOV video.'
  if (error === 'image_too_large') return `Hero image is too large. Keep it under ${formatFileSize(maxBytes ?? MAX_HERO_IMAGE_BYTES)}.`
  if (error === 'video_too_large') return `Hero video is too large. Keep it under ${formatFileSize(maxBytes ?? MAX_HERO_VIDEO_BYTES)}.`
  if (error === 'storage_size_limit' || error.toLowerCase().includes('maximum allowed size')) {
    return `Upload failed because storage is still capped below this file. Try once more; if it repeats, paste a public hero media URL for now.`
  }
  if (error.includes('Upload failed')) return error
  return 'Upload failed. Please try again, or paste a public hero media URL for now.'
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not saved yet'
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

export default function AdminContentPage() {
  const { isLoggedIn, loading, status, rights } = useAuth()
  const [content, setContent] = useState<HomeContent>(DEFAULT_CONTENT)
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  const [source, setSource] = useState('default')
  const [loadingContent, setLoadingContent] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false)
  const [uploadingHeroVideo, setUploadingHeroVideo] = useState(false)
  const [uploadHelpOpen, setUploadHelpOpen] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const uploadHelpRef = useRef<HTMLDivElement | null>(null)

  const canAdmin = isLoggedIn && status === 'approved' && rights['admin.content.manage'] === true

  async function loadContent() {
    setLoadingContent(true)
    setError(null)
    try {
      const res = await fetch('/api/admin/content', { credentials: 'include' })
      const data = await res.json() as ContentResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to load content')
      setContent(data.content ?? DEFAULT_CONTENT)
      setUpdatedAt(data.updatedAt ?? null)
      setSource(data.warning === 'migration_required' ? 'migration required' : (data.source ?? 'default'))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load content')
    } finally {
      setLoadingContent(false)
    }
  }

  useEffect(() => {
    if (canAdmin) void loadContent()
  }, [canAdmin])

  useEffect(() => {
    if (!uploadHelpOpen) return undefined
    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node | null
      if (target && uploadHelpRef.current?.contains(target)) return
      setUploadHelpOpen(false)
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setUploadHelpOpen(false)
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [uploadHelpOpen])

  function updatePlatformLink(platform: Platform, value: string) {
    setContent(prev => ({
      ...prev,
      ...(platform === 'facebook' ? { facebookVideoUrl: value } : {}),
      ...(platform === 'youtube' ? { youtubeVideoUrl: value } : {}),
      platformLinks: { ...prev.platformLinks, [platform]: value },
    }))
  }

  function updateVideoUrl(platform: 'facebook' | 'youtube', value: string) {
    setContent(prev => ({
      ...prev,
      ...(platform === 'facebook' ? { facebookVideoUrl: value } : { youtubeVideoUrl: value }),
      platformLinks: { ...prev.platformLinks, [platform]: value },
    }))
  }

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/admin/content', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content }),
      })
      const data = await res.json() as ContentResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to save content')
      setContent(data.content ?? content)
      setUpdatedAt(data.updatedAt ?? null)
      setSource(data.source ?? 'database')
      setMessage('Public home content saved.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save content')
    } finally {
      setSaving(false)
    }
  }

  async function uploadHeroMedia(file: File | null, mediaType: HomeContent['heroMediaType']) {
    if (!file) return
    setMessage(null)
    setError(null)
    const isImage = mediaType === 'image'
    const allowedTypes = isImage ? ALLOWED_HERO_IMAGE_TYPES : ALLOWED_HERO_VIDEO_TYPES
    const maxBytes = isImage ? MAX_HERO_IMAGE_BYTES : MAX_HERO_VIDEO_BYTES
    const mediaLabel = isImage ? 'image' : 'video'
    if (!allowedTypes.has(file.type)) {
      setError(isImage ? 'Upload a JPG, PNG, WebP, or AVIF image.' : 'Upload an MP4, WebM, or MOV video.')
      return
    }
    if (file.size > maxBytes) {
      setError(`Hero ${mediaLabel} is ${formatFileSize(file.size)}. Keep uploads under ${formatFileSize(maxBytes)}.`)
      return
    }
    if (isImage) setUploadingHeroImage(true)
    else setUploadingHeroVideo(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch('/api/admin/content/media', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      const data = await res.json() as { url?: string; error?: string; maxBytes?: number; mediaType?: HomeContent['heroMediaType'] }
      if (!res.ok || !data.url) throw new Error(uploadErrorMessage(data.error ?? `Unable to upload hero ${mediaLabel}`, data.maxBytes))
      const url = data.url
      const nextMediaType = data.mediaType === 'image' || data.mediaType === 'video' ? data.mediaType : mediaType
      setContent(prev => nextMediaType === 'image'
        ? { ...prev, heroMediaType: 'image', heroImageUrl: url }
        : { ...prev, heroMediaType: 'video', heroVideoUrl: url })
      setMessage(`Hero ${mediaLabel} uploaded. Save public home to publish it.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to upload hero ${mediaLabel}`)
    } finally {
      if (isImage) setUploadingHeroImage(false)
      else setUploadingHeroVideo(false)
    }
  }

  if (loading) {
    return (
      <div className="admin-page">
        <p className="admin-muted">Checking admin access...</p>
      </div>
    )
  }

  if (!isLoggedIn) return <Navigate to="/signin" replace />

  if (!canAdmin) {
    return (
      <div className="admin-page">
        <span className="legal-eyebrow">Admin</span>
        <h1 className="admin-heading">Admin access required.</h1>
        <p className="admin-muted">This page is only available to approved Yan-Vada admins.</p>
        <Link to="/" className="btn btn-secondary">Back to pitch</Link>
      </div>
    )
  }

  const primaryVideoUrl = content.primaryVideoPlatform === 'facebook' ? content.facebookVideoUrl : content.youtubeVideoUrl
  const primaryEmbedUrl = getVideoEmbedUrl(content.primaryVideoPlatform, primaryVideoUrl)

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <span className="legal-eyebrow">CMS · legacy public home</span>
          <h1 className="admin-heading">Legacy public home</h1>
          <p className="admin-muted">
            This editor is frozen. Master Pitch CMS is the authoritative source and all active audience or sponsor editions should stem from that version.
          </p>
        </div>
        <div className="admin-stats" aria-label="Content status">
          <div><strong>{source}</strong><span>Source</span></div>
          <div><strong>{content.primaryVideoPlatform}</strong><span>Primary</span></div>
          <div><strong>{formatDate(updatedAt)}</strong><span>Updated</span></div>
        </div>
      </header>

      {(message || error) && (
        <div className={`admin-alert${error ? ' admin-alert-error' : ''}`} role={error ? 'alert' : 'status'}>
          {error ?? message}
        </div>
      )}

      {LEGACY_CMS_FROZEN && (
        <div className="admin-alert" role="status">
          Public home editing is frozen. Use <Link to="/admin/sponsor-pages/master">Master Pitch CMS</Link> for the active root audience surface and all downstream versions.
        </div>
      )}

      {loadingContent ? (
        <div className="admin-card admin-content-form"><p className="admin-muted">Loading content...</p></div>
      ) : (
        <form className="admin-card admin-content-form" onSubmit={handleSave}>
          <fieldset disabled={LEGACY_CMS_FROZEN} style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}>
          <section className="admin-content-section">
            <div>
              <h2>Hero media</h2>
              <p className="admin-muted">Swap the public-home hero between the sunset image and a looping muted video.</p>
            </div>
            <label>
              <span>Hero mode</span>
              <select
                value={content.heroMediaType}
                onChange={e => setContent(prev => ({ ...prev, heroMediaType: e.target.value as HomeContent['heroMediaType'] }))}
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </label>
            <label>
              <span>Hero image URL</span>
              <input
                type="text"
                value={content.heroImageUrl}
                onChange={e => setContent(prev => ({ ...prev, heroImageUrl: e.target.value }))}
                placeholder="/sunset-1.jpeg"
              />
            </label>
            <div className="admin-upload-field">
              <label htmlFor="hero-image-upload"><span>Upload/change hero image</span></label>
              <input
                id="hero-image-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif"
                disabled={uploadingHeroImage}
                onChange={e => {
                  const file = e.currentTarget.files?.[0] ?? null
                  e.currentTarget.value = ''
                  void uploadHeroMedia(file, 'image')
                }}
              />
            </div>
            <label>
              <span>Hero video URL</span>
              <input
                type="text"
                value={content.heroVideoUrl}
                onChange={e => setContent(prev => ({ ...prev, heroVideoUrl: e.target.value }))}
                placeholder="Upload a video or paste an MP4/WebM URL"
              />
            </label>
            <div className="admin-upload-field">
              <div className="admin-label-with-info">
                <label htmlFor="hero-video-upload">Upload/change hero video</label>
                <div ref={uploadHelpRef} className={`admin-info-popover${uploadHelpOpen ? ' open' : ''}`}>
                  <button
                    type="button"
                    className="admin-info-button"
                    aria-label="Hero upload guidance"
                    aria-expanded={uploadHelpOpen}
                    aria-controls={HERO_UPLOAD_HELP_ID}
                    aria-describedby={HERO_UPLOAD_HELP_ID}
                    onClick={() => setUploadHelpOpen(prev => !prev)}
                  >
                    i
                  </button>
                  <div id={HERO_UPLOAD_HELP_ID} className="admin-info-bubble" role="tooltip">
                    Upload MP4, WebM, or MOV under {formatFileSize(MAX_HERO_VIDEO_BYTES)}. For smooth hero playback, keep the exported loop closer to 10 MB when possible.
                  </div>
                </div>
              </div>
              <input
                id="hero-video-upload"
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                disabled={uploadingHeroVideo}
                onChange={e => {
                  const file = e.currentTarget.files?.[0] ?? null
                  e.currentTarget.value = ''
                  void uploadHeroMedia(file, 'video')
                }}
              />
            </div>
            <div className="admin-hero-media-preview">
              {content.heroMediaType === 'video' && content.heroVideoUrl ? (
                <video src={content.heroVideoUrl} poster={content.heroImageUrl} autoPlay muted loop playsInline controls />
              ) : (
                <img src={content.heroImageUrl || '/sunset-1.jpeg'} alt="Current public-home hero" />
              )}
            </div>
            {uploadingHeroImage && <p className="admin-muted admin-content-upload-note">Uploading image...</p>}
            {uploadingHeroVideo && <p className="admin-muted admin-content-upload-note">Uploading video...</p>}
          </section>

          <section className="admin-content-section">
            <div>
              <h2>Video</h2>
              <p className="admin-muted">Paste normal watch URLs. Facebook share links are saved as direct links because Facebook often blocks them inside iframes.</p>
            </div>
            <label>
              <span>Primary video platform</span>
              <select
                value={content.primaryVideoPlatform}
                onChange={e => setContent(prev => ({ ...prev, primaryVideoPlatform: e.target.value as HomeContent['primaryVideoPlatform'] }))}
              >
                <option value="facebook">Facebook</option>
                <option value="youtube">YouTube</option>
              </select>
            </label>
            <label>
              <span>Video orientation</span>
              <select
                value={content.videoOrientation}
                onChange={e => setContent(prev => ({ ...prev, videoOrientation: e.target.value as HomeContent['videoOrientation'] }))}
              >
                <option value="vertical">Vertical</option>
                <option value="landscape">Landscape</option>
              </select>
            </label>
            <label>
              <span>Facebook video URL</span>
              <input
                type="url"
                value={content.facebookVideoUrl}
                onChange={e => updateVideoUrl('facebook', e.target.value)}
                placeholder="https://www.facebook.com/share/v/..."
              />
            </label>
            <label>
              <span>YouTube video URL</span>
              <input
                type="url"
                value={content.youtubeVideoUrl}
                onChange={e => updateVideoUrl('youtube', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </label>
            <div className={`admin-video-preview public-video-frame public-video-${content.videoOrientation}`}>
              {primaryEmbedUrl ? (
                <iframe
                  title="Public home video preview"
                  src={primaryEmbedUrl}
                  loading="lazy"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : primaryVideoUrl ? (
                <a className="public-video-fallback" href={primaryVideoUrl} target="_blank" rel="noopener noreferrer">
                  Open video directly. This URL does not produce an embed preview.
                </a>
              ) : (
                <div className="public-video-fallback">Add a primary video URL to preview it here.</div>
              )}
            </div>
          </section>

          <section className="admin-content-section">
            <div>
              <h2>Platform links</h2>
              <p className="admin-muted">Facebook and YouTube mirror the video URLs. Leave the other platforms blank to show them as coming soon.</p>
            </div>
            <div className="admin-platform-grid">
              {PLATFORM_LABELS.map(platform => (
                <label key={platform.key}>
                  <span>{platform.label}</span>
                  <input
                    type="url"
                    value={content.platformLinks[platform.key] ?? ''}
                    onChange={e => updatePlatformLink(platform.key, e.target.value)}
                    placeholder={platform.placeholder}
                  />
                </label>
              ))}
            </div>
          </section>

          <div className="admin-member-actions">
            <button className="btn btn-primary" type="submit" disabled={saving || LEGACY_CMS_FROZEN}>
              {LEGACY_CMS_FROZEN ? 'Editing frozen' : (saving ? 'Saving...' : 'Save public home')}
            </button>
            <Link className="btn btn-secondary" to="/home">View /home</Link>
            <Link className="btn btn-secondary" to="/admin/sponsor-pages/master">Open Master Pitch CMS</Link>
          </div>
          </fieldset>
        </form>
      )}
    </div>
  )
}
